import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { ApolloProvider } from '@apollo/client';
import { Spinner } from '@semsis/ui/Spinner';
import { ToastProvider } from '@semsis/ui/Toast';
import { ThemeProvider } from '@semsis/ui/theme';
import { AuthProvider, LoginPage } from '@semsis/auth-frontend';
import { createApolloClient } from '@referee/graphql-client';
import { ErrorBoundary } from '@referee/error-handling';
import logo from './assets/logo.svg';
import { AdminLayout } from './layouts/AdminLayout';
import { CatalogLayout } from './layouts/CatalogLayout';

const Dashboard = lazy(() => import('./pages/home/Dashboard').then((m) => ({ default: m.Dashboard })));
const FederationList = lazy(() => import('./pages/federations/FederationList').then((m) => ({ default: m.FederationList })));
const FederationForm = lazy(() => import('./pages/federations/FederationForm').then((m) => ({ default: m.FederationForm })));
const FederationDetail = lazy(() => import('./pages/federations/FederationDetail').then((m) => ({ default: m.FederationDetail })));
const CatalogIndex = lazy(() => import('./pages/catalogs/CatalogIndex').then((m) => ({ default: m.CatalogIndex })));
const CatalogForm = lazy(() => import('./pages/catalogs/CatalogForm').then((m) => ({ default: m.CatalogForm })));
const CatalogDetail = lazy(() => import('./pages/catalogs/CatalogDetail').then((m) => ({ default: m.CatalogDetail })));
const CatalogVersionForm = lazy(() => import('./pages/catalogs/CatalogVersionForm').then((m) => ({ default: m.CatalogVersionForm })));
const CatalogVersionPreview = lazy(() => import('./pages/catalogs/CatalogVersionPreview').then((m) => ({ default: m.CatalogVersionPreview })));
const UserList = lazy(() => import('./pages/users/UserList').then((m) => ({ default: m.UserList })));
const UserForm = lazy(() => import('./pages/users/UserForm').then((m) => ({ default: m.UserForm })));
const UserDetail = lazy(() => import('./pages/users/UserDetail').then((m) => ({ default: m.UserDetail })));
const SessionDetail = lazy(() => import('./pages/sessions/SessionDetail').then((m) => ({ default: m.SessionDetail })));

const client = createApolloClient({
  graphqlEndpoint: '/graphql',
  onAuthError: () => {
    sessionStorage.removeItem('semsis_auth_token');
    window.location.reload();
  },
});

export function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <ToastProvider>
          <ApolloProvider client={client}>
            <AuthProvider>
              <BrowserRouter>
                <Suspense fallback={<Spinner page />}>
                <Routes>
                  <Route path="/login" element={<LoginPage logo={logo} />} />
                  <Route element={<AdminLayout />}>
                    <Route index element={<Dashboard />} />
                    <Route path="federations" element={<FederationList />} />
                    <Route path="federations/create" element={<FederationForm />} />
                    <Route path="federations/:id/edit" element={<FederationForm />} />
                    <Route path="federations/:id/*" element={<FederationDetail />} />
                    <Route path="federations/:orgId/sessions/:sessionId" element={<SessionDetail />} />
                    <Route path="users" element={<UserList />} />
                    <Route path="users/create" element={<UserForm />} />
                    <Route path="users/:id" element={<UserDetail />} />
                    <Route path="catalogs" element={<CatalogLayout />}>
                      <Route index element={<CatalogIndex />} />
                      <Route path="create" element={<CatalogForm />} />
                      <Route path=":groupId" element={<CatalogDetail />} />
                      <Route path=":groupId/versions/create" element={<CatalogVersionForm />} />
                      <Route path=":groupId/versions/:versionId/preview" element={<CatalogVersionPreview />} />
                    </Route>
                  </Route>
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
              </BrowserRouter>
            </AuthProvider>
          </ApolloProvider>
        </ToastProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}
